/**
 * Landing page Redux: load composition from GET /api/landing (SSR + client).
 */

import axios from 'axios';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { apiBaseUrl } from '../../util/api';

// ================ Action types ================ //

export const FETCH_LANDING_REQUEST = 'app/LandingPage/FETCH_LANDING_REQUEST';
export const FETCH_LANDING_SUCCESS = 'app/LandingPage/FETCH_LANDING_SUCCESS';
export const FETCH_LANDING_ERROR = 'app/LandingPage/FETCH_LANDING_ERROR';

// ================ Reducer ================ //

const initialState = {
  inProgress: false,
  error: null,
  seo: null,
  blocks: [],
  blogPosts: [],
  /** blockId -> ordered listing id strings */
  listingIdsByBlock: {},
  updatedAt: null,
};

const landingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LANDING_REQUEST:
      return { ...state, inProgress: true, error: null };
    case FETCH_LANDING_SUCCESS:
      return {
        ...state,
        inProgress: false,
        error: null,
        seo: payload.seo,
        blocks: payload.blocks,
        blogPosts: payload.blogPosts,
        listingIdsByBlock: payload.listingIdsByBlock,
        updatedAt: payload.updatedAt,
      };
    case FETCH_LANDING_ERROR:
      return { ...state, inProgress: false, error: payload };
    default:
      return state;
  }
};

export default landingPageReducer;

// ================ Helpers ================ //

const landingApiRequest = (marketplaceRootURL) => {
  const baseUrl = apiBaseUrl(marketplaceRootURL);
  const url = `${baseUrl}/api/landing`;
  return axios
    .get(url, {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    })
    .then(response => response.data)
    .catch(error => {
      if (error.response) {
        const e = new Error(
          error.response.data?.message || `HTTP error! status: ${error.response.status}`
        );
        Object.assign(e, error.response.data || {});
        throw e;
      }
      throw error;
    });
};

/**
 * Build blockId -> ordered listing uuid[] from composition + hydrated map.
 */
export const buildListingIdsByBlock = (blocks, listingsById = {}) => {
  const out = {};
  (blocks || []).forEach(block => {
    if (!block?.id) return;
    if (block.type === 'productCarousel') {
      if (block.source === 'category' && Array.isArray(block.resolvedListingIds)) {
        out[block.id] = block.resolvedListingIds.map(String);
      } else if (Array.isArray(block.listingIds)) {
        out[block.id] = block.listingIds.map(String);
      } else {
        out[block.id] = [];
      }
    } else if (block.type === 'featuredProduct' && block.productId) {
      out[block.id] = [String(block.productId)];
    }
  });
  // Drop ids missing from hydrate (unpublished / deleted)
  Object.keys(out).forEach(blockId => {
    out[blockId] = out[blockId].filter(id => listingsById[id] || listingsById[String(id)]);
  });
  return out;
};

// ================ Thunks ================ //

export const fetchLandingPage = (config = {}) => dispatch => {
  const marketplaceRootURL = config?.marketplaceRootURL;
  const baseUrl = apiBaseUrl(marketplaceRootURL);
  if (!baseUrl && typeof window === 'undefined') {
    return Promise.resolve();
  }

  dispatch({ type: FETCH_LANDING_REQUEST });

  return landingApiRequest(marketplaceRootURL)
    .then(payload => {
      const listingsById = payload.listingsById || {};
      const included = payload.included || [];
      const listingData = Object.values(listingsById);

      if (listingData.length || included.length) {
        dispatch(
          addMarketplaceEntities(
            { data: { data: listingData, included } },
            { listingFields: config?.listing?.listingFields }
          )
        );
      }

      const blocks = payload.blocks || [];
      const listingIdsByBlock = buildListingIdsByBlock(blocks, listingsById);

      dispatch({
        type: FETCH_LANDING_SUCCESS,
        payload: {
          seo: payload.seo || {},
          blocks,
          blogPosts: payload.blogPosts || [],
          listingIdsByBlock,
          updatedAt: payload.updatedAt || null,
        },
      });
      return payload;
    })
    .catch(e => {
      log.error(e, 'fetch-landing-page-failed');
      dispatch({ type: FETCH_LANDING_ERROR, payload: storableError(e) });
      throw e;
    });
};

export const loadData = (params, search, config) => (dispatch, getState) => {
  const existing = getState()?.LandingPage;
  // SSR already hydrated: skip the duplicate client round-trip (was ~10–20s).
  if (existing?.blocks?.length && !existing.error) {
    return Promise.resolve();
  }
  return dispatch(fetchLandingPage(config)).catch(() => null);
};

// Keep export so old imports don't break during HMR
export const LANDING_FEATURED_BLOG_COUNT = 4;
export const ASSET_NAME = 'landing-page';
